import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import SelectField from "component/Select/Select";
import ApiRequest from 'utils/ApiRequest';
import API from 'constants/api';

const ListSelectField = ({
  label,
  placeholder,
  value,
  onChange,
  required = false,
  errorText,
  showHelperArea = false,
  validateValue,
  user,
  disabled = false,
  className,
  fill = false,
  showAllOption = false,
  showTheme = false,
  groupByName = false,
  translate
}) => {
  const [listsOptions, setListsOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadLists = async () => {
      const token = user?.dsToken;
      if (!token) {
        setListsOptions([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const api = new ApiRequest(token);
        if (showTheme) {
          const [listsResponse, themesResponse] = await Promise.all([
            api.get(API.FETCH_PRICEBANNER_LISTS),
            api.get(API.FETCH_PRICEBANNER_THEMES)
          ]);
          if (listsResponse && listsResponse.data && themesResponse && themesResponse.data) {
            const listsData = listsResponse.data;
            const themesData = themesResponse.data;
            const themesMap = {};
            if (Array.isArray(themesData)) {
              themesData.forEach(theme => {
                themesMap[theme.code] = theme.name;
              });
            }
            if (Array.isArray(listsData)) {
              let lists = listsData.map(list => {
                const listName = list.name || list.listName || 'Lista sem nome';
                const {themeCode} = list;
                const themeName = themeCode ? themesMap[themeCode] : null;
                const label = themeName ? `${listName} | ${themeName}` : listName;
                return {
                  value: list.idPriceBannerList || list.id,
                  label,
                  originalName: listName,
                  ...(list.idBranches && { idBranches: list.idBranches }),
                  ...(list.dtStart && { dtStart: list.dtStart }),
                  ...(list.dtEnd && { dtEnd: list.dtEnd }),
                  ...(list && { fullData: list })

                };
              });
              if (groupByName) {
                const groupedLists = {};
                lists.forEach(list => {
                  const name = list.originalName.trim().toLowerCase();
                  if (!groupedLists[name]) {
                    groupedLists[name] = {
                      value: [list.value],
                      label: list.originalName,
                      originalName: name,
                      allIds: [list.value]
                    };
                  } else {
                    if (!groupedLists[name].allIds.includes(list.value)) {
                      groupedLists[name].allIds.push(list.value);
                      groupedLists[name].value.push(list.value);
                    }
                  }
                });
                lists = Object.values(groupedLists);
              }
              const finalLists = showAllOption ? [
                {
                  label: translate('option.all'),
                  value: groupByName ? ["todos"] : "todos",
                },
                ...lists
              ] : lists;
              setListsOptions(finalLists);
            } else {
              setListsOptions([]);
            }
          } else {
            setListsOptions([]);
          }
        } else {
          const response = await api.get(API.FETCH_PRICEBANNER_LISTS);
          if (response && response.data) {
            const listsData = response.data;
            if (Array.isArray(listsData)) {
              let lists = listsData.map(list => ({
                value: list.idPriceBannerList || list.id,
                label: list.name || list.listName || 'Lista sem nome',
                originalName: list.name || list.listName || 'Lista sem nome'
              }));
              if (groupByName) {
                const groupedLists = {};
                lists.forEach(list => {
                  const name = list.originalName.trim().toLowerCase();
                  if (!groupedLists[name]) {
                    groupedLists[name] = {
                      value: [list.value],
                      label: list.originalName,
                      originalName: name,
                      allIds: [list.value]
                    };
                  } else {
                    if (!groupedLists[name].allIds.includes(list.value)) {
                      groupedLists[name].allIds.push(list.value);
                      groupedLists[name].value.push(list.value);
                    }
                  }
                });
                lists = Object.values(groupedLists);
              }
              const finalLists = showAllOption ? [
                {
                  label: translate('option.all'),
                  value: groupByName ? ["todos"] : "todos",
                },
                ...lists
              ] : lists;
              setListsOptions(finalLists);
            } else {
              setListsOptions([]);
            }
          } else {
            setListsOptions([]);
          }
        }
      } catch (error) {
        setListsOptions([]);
      } finally {
        setLoading(false);
      }
    };
    loadLists();
  }, [user?.dsToken, showTheme, groupByName]);


  const defaultValidateValue = (value) => {
    if (required && (!value || !value.value)) {
      return false;
    }
    return true;
  };

  const finalValidateValue = validateValue || defaultValidateValue;
  const finalErrorText = errorText;
  const placeholderLabel = (loading && !value) ? translate('loading') : placeholder;

  return (
    <SelectField
      label={label}
      placeholder={placeholderLabel}
      value={value}
      options={listsOptions}
      onChange={onChange}
      required={required}
      errorText={finalErrorText}
      showHelperArea={showHelperArea}
      validateValue={finalValidateValue}
      disabled={disabled || (loading && !value)}
      className={className}
      fill={fill}
    />
  );
};

ListSelectField.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  errorText: PropTypes.string,
  showHelperArea: PropTypes.bool,
  validateValue: PropTypes.func,
  user: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  fill: PropTypes.bool,
  showAllOption: PropTypes.bool,
  showTheme: PropTypes.bool,
  groupByName: PropTypes.bool,
  translate: PropTypes.func.isRequired
};

export default translate(null, {
  wait: true,
  translateFuncName: 'translate',
})(ListSelectField);